import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DataTableColumn, FilterRule } from '../../../types/grid';
import {
  OperatorDef,
  SearchBarStage,
  normalizeDateValue,
  isBooleanColumn,
  isEnumColumn,
  getColumnOptions,
  getColumnName,
} from './types';

interface UseFilterSearchStateProps<T extends Record<string, any>> {
  columns: DataTableColumn<T>[];
  filterRules: FilterRule[];
  onFilterRulesChange: (rules: FilterRule[]) => void;
  searchValue: string;
  onSearchChange: (search: string) => void;
  externalInputRef?: React.RefObject<HTMLInputElement | null>;
}

export function useFilterSearchState<T extends Record<string, any>>({
  columns,
  filterRules,
  onFilterRulesChange,
  searchValue,
  onSearchChange,
  externalInputRef,
}: UseFilterSearchStateProps<T>) {
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [stage, setStage] = useState<SearchBarStage>('column');
  const [pendingColumn, setPendingColumn] = useState<DataTableColumn<T> | null>(null);
  const [pendingOperator, setPendingOperator] = useState<OperatorDef | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = (node: HTMLInputElement | null) => {
    localInputRef.current = node;
    if (externalInputRef) {
      (externalInputRef as any).current = node;
    }
  };

  // Filterable columns list
  const filterableColumns = useMemo(() => {
    return columns.filter(
      (col) => col.filterable !== false && !col.isSystem && col.id !== 'select' && col.id !== 'actions'
    );
  }, [columns]);

  // Standard operators available for non-boolean and non-enum columns
  const availableOperators = useMemo<OperatorDef[]>(() => {
    if (!pendingColumn) return [];
    const type = pendingColumn.dataType || 'text';

    if (type === 'number') {
      return [
        { op: 'equals', symbol: '=', label: 'is' },
        { op: 'not_equals', symbol: '!=', label: 'is not' },
        { op: 'greater_than', symbol: '>', label: 'is greater than' },
        { op: 'less_than', symbol: '<', label: 'is less than' },
        { op: 'is_empty', symbol: 'is empty', label: 'has no value' },
        { op: 'is_not_empty', symbol: 'is not empty', label: 'has a value' },
      ];
    }
    if (type === 'date') {
      return [
        { op: 'is_on', symbol: '=', label: 'on date' },
        { op: 'is_before', symbol: '<', label: 'before date' },
        { op: 'is_after', symbol: '>', label: 'after date' },
        { op: 'is_empty', symbol: 'is empty', label: 'has no value' },
        { op: 'is_not_empty', symbol: 'is not empty', label: 'has a value' },
      ];
    }
    return [
      { op: 'equals', symbol: '=', label: 'is' },
      { op: 'not_equals', symbol: '!=', label: 'is not' },
      { op: 'contains', symbol: 'contains', label: 'contains text' },
      { op: 'greater_than', symbol: '>', label: 'is greater than' },
      { op: 'less_than', symbol: '<', label: 'is less than' },
      { op: 'starts_with', symbol: '^', label: 'starts with' },
      { op: 'is_empty', symbol: 'is empty', label: 'has no value' },
      { op: 'is_not_empty', symbol: 'is not empty', label: 'has a value' },
    ];
  }, [pendingColumn]);

  // Boolean options list
  const booleanOptions = useMemo(
    () => [
      { label: 'is true', value: 'true' },
      { label: 'is false', value: 'false' },
    ],
    []
  );

  const matchingBooleanOptions = useMemo(() => {
    if (stage !== 'boolean') return [];
    const q = inputValue.toLowerCase().trim();
    if (!q) return booleanOptions;
    return booleanOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [stage, inputValue, booleanOptions]);

  // Enum options for pending column
  const matchingEnumOptions = useMemo(() => {
    if (stage !== 'enum' || !pendingColumn) return [];
    const opts = getColumnOptions(pendingColumn);
    const q = inputValue.toLowerCase().trim();
    if (!q) return opts;
    return opts.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [stage, pendingColumn, inputValue]);

  // Options for pending column if value selection stage (for non-enum columns)
  const pendingOptions = useMemo(() => {
    if (!pendingColumn) return [];
    return getColumnOptions(pendingColumn);
  }, [pendingColumn]);

  // Filter columns list by current input value when stage is 'column'
  const matchingColumns = useMemo(() => {
    if (stage !== 'column') return [];
    const q = inputValue.toLowerCase().trim();
    if (!q) return filterableColumns;
    return filterableColumns.filter((col) => {
      const name = getColumnName(col).toLowerCase();
      return name.includes(q) || col.id.toLowerCase().includes(q);
    });
  }, [stage, inputValue, filterableColumns]);

  // Filter operators by current input when stage is 'operator'
  const matchingOperators = useMemo(() => {
    if (stage !== 'operator') return [];
    const q = inputValue.toLowerCase().trim();
    if (!q) return availableOperators;
    return availableOperators.filter(
      (op) =>
        op.symbol.toLowerCase().includes(q) ||
        op.label.toLowerCase().includes(q) ||
        op.op.toLowerCase().includes(q)
    );
  }, [stage, inputValue, availableOperators]);

  // Filter options by current input when stage is 'value'
  const matchingOptions = useMemo(() => {
    if (stage !== 'value' || pendingOptions.length === 0) return [];
    const q = inputValue.toLowerCase().trim();
    if (!q) return pendingOptions;
    return pendingOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [stage, inputValue, pendingOptions]);

  // Total selectable items in current dropdown stage
  const currentItemsCount = useMemo(() => {
    if (stage === 'column') {
      const hasSearchOption = inputValue.trim().length > 0 ? 1 : 0;
      return matchingColumns.length + hasSearchOption;
    }
    if (stage === 'boolean') {
      return matchingBooleanOptions.length;
    }
    if (stage === 'enum') {
      return matchingEnumOptions.length;
    }
    if (stage === 'operator') {
      return matchingOperators.length;
    }
    if (stage === 'value') {
      if (pendingOptions.length > 0) {
        return matchingOptions.length;
      }
      return inputValue.trim().length > 0 ? 1 : 0;
    }
    return 0;
  }, [
    stage,
    matchingColumns,
    matchingBooleanOptions,
    matchingEnumOptions,
    matchingOperators,
    matchingOptions,
    pendingOptions,
    inputValue,
  ]);

  // Reset highlighted index on input or stage changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [stage, inputValue]);

  // Stage transitions
  const handleSelectColumn = (col: DataTableColumn<T>) => {
    setPendingColumn(col);
    setInputValue('');

    if (isBooleanColumn(col)) {
      setStage('boolean');
      setDropdownOpen(true);
      localInputRef.current?.focus();
      return;
    }

    if (isEnumColumn(col)) {
      setStage('enum');
      setDropdownOpen(true);
      localInputRef.current?.focus();
      return;
    }

    setStage('operator');
    setDropdownOpen(true);
    localInputRef.current?.focus();
  };

  const handleCommitBoolean = (val: string) => {
    if (!pendingColumn) return;
    const newRule: FilterRule = {
      id: Math.random().toString(36).substring(2, 9),
      columnKey: pendingColumn.id,
      operator: 'boolean',
      value: val,
    };
    onFilterRulesChange([...filterRules, newRule]);
    resetPendingState();
  };

  const handleCommitEnum = (val: string) => {
    if (!pendingColumn) return;
    const newRule: FilterRule = {
      id: Math.random().toString(36).substring(2, 9),
      columnKey: pendingColumn.id,
      operator: 'equals',
      value: val,
    };
    onFilterRulesChange([...filterRules, newRule]);
    resetPendingState();
  };

  const handleSelectOperator = (opDef: OperatorDef) => {
    if (opDef.op === 'boolean') {
      handleCommitBoolean(opDef.value || 'true');
      return;
    }

    if (opDef.op === 'is_empty' || opDef.op === 'is_not_empty') {
      const newRule: FilterRule = {
        id: Math.random().toString(36).substring(2, 9),
        columnKey: pendingColumn!.id,
        operator: opDef.op,
        value: '',
      };
      onFilterRulesChange([...filterRules, newRule]);
      resetPendingState();
      return;
    }

    setPendingOperator(opDef);
    setInputValue('');
    setStage('value');
    setDropdownOpen(true);
    localInputRef.current?.focus();
  };

  const handleCommitValue = (val: string) => {
    if (!pendingColumn || !pendingOperator) return;
    let cleanVal = val.trim();
    if (!cleanVal && pendingOperator.op !== 'is_empty' && pendingOperator.op !== 'is_not_empty') {
      return;
    }

    if (pendingColumn.dataType === 'date') {
      cleanVal = normalizeDateValue(cleanVal);
    }

    const newRule: FilterRule = {
      id: Math.random().toString(36).substring(2, 9),
      columnKey: pendingColumn.id,
      operator: pendingOperator.op,
      value: cleanVal,
    };
    onFilterRulesChange([...filterRules, newRule]);
    resetPendingState();
  };

  const handleCommitSearchText = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    onSearchChange(clean);
    setInputValue('');
    setDropdownOpen(false);
  };

  const resetPendingState = () => {
    setPendingColumn(null);
    setPendingOperator(null);
    setInputValue('');
    setStage('column');
    setDropdownOpen(false);
  };

  const handleRemoveFilterRule = (ruleId: string) => {
    onFilterRulesChange(filterRules.filter((r) => r.id !== ruleId));
  };

  const handleRemoveSearchText = () => {
    onSearchChange('');
  };

  // Keyboard navigation inside the search bar
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (pendingColumn) {
        resetPendingState();
      } else {
        setDropdownOpen(false);
      }
      return;
    }

    if (e.key === 'Backspace' && inputValue === '') {
      if (stage === 'value') {
        e.preventDefault();
        setPendingOperator(null);
        setStage('operator');
        return;
      }
      if (stage === 'operator' || stage === 'boolean' || stage === 'enum') {
        e.preventDefault();
        setPendingColumn(null);
        setStage('column');
        return;
      }
      if (filterRules.length > 0) {
        e.preventDefault();
        onFilterRulesChange(filterRules.slice(0, -1));
        return;
      }
      if (searchValue) {
        e.preventDefault();
        onSearchChange('');
        return;
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdownOpen) {
        setDropdownOpen(true);
        return;
      }
      setHighlightedIndex((prev) => (prev + 1) % Math.max(1, currentItemsCount));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? Math.max(0, currentItemsCount - 1) : prev - 1
      );
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (stage === 'column') {
        if (matchingColumns.length > 0 && highlightedIndex < matchingColumns.length) {
          handleSelectColumn(matchingColumns[highlightedIndex]);
        } else if (inputValue.trim()) {
          handleCommitSearchText(inputValue);
        }
        return;
      }

      if (stage === 'boolean') {
        if (matchingBooleanOptions.length > 0 && highlightedIndex < matchingBooleanOptions.length) {
          handleCommitBoolean(matchingBooleanOptions[highlightedIndex].value);
        }
        return;
      }

      if (stage === 'enum') {
        if (matchingEnumOptions.length > 0 && highlightedIndex < matchingEnumOptions.length) {
          handleCommitEnum(matchingEnumOptions[highlightedIndex].value);
        }
        return;
      }

      if (stage === 'operator') {
        if (matchingOperators.length > 0 && highlightedIndex < matchingOperators.length) {
          handleSelectOperator(matchingOperators[highlightedIndex]);
        }
        return;
      }

      if (stage === 'value') {
        if (pendingOptions.length > 0 && highlightedIndex < matchingOptions.length) {
          handleCommitValue(matchingOptions[highlightedIndex].value);
        } else if (inputValue.trim()) {
          handleCommitValue(inputValue);
        }
        return;
      }
    }
  };

  return {
    isFocused,
    setIsFocused,
    dropdownOpen,
    setDropdownOpen,
    inputValue,
    setInputValue,
    stage,
    setStage,
    pendingColumn,
    pendingOperator,
    highlightedIndex,
    setHighlightedIndex,
    containerRef,
    localInputRef,
    setInputRef,
    matchingColumns,
    matchingBooleanOptions,
    matchingEnumOptions,
    matchingOperators,
    matchingOptions,
    pendingOptions,
    currentItemsCount,
    handleSelectColumn,
    handleCommitBoolean,
    handleCommitEnum,
    handleSelectOperator,
    handleCommitValue,
    handleCommitSearchText,
    resetPendingState,
    handleRemoveFilterRule,
    handleRemoveSearchText,
    handleKeyDown,
  };
}
